import React, { useState } from "react";
import { Link } from "react-router-dom";
import { makeDelete, makeGet } from "@culturehq/client";
import { Loader, Pagination, Table, Warning, Icon, Button, Tag } from "@culturehq/components";

import { QueryStringConsumer, QueryStringProvider } from "app/context/QueryStringContext";
import { useFetch, usePagination } from "app/hooks";
import FacebookLink from "app/links/FacebookLink";
import InstagramLink from "app/links/InstagramLink";
import LinkedinLink from "app/links/LinkedinLink";
import TiktokLink from "app/links/TiktokLink";
import { isMultiImageStory, t, Trans } from "app/utils";

import SocialChannel from "components/common/SocialChannel";
import { Time } from "components/common/Time";
import PageHeader from "components/display/PageHeader";
import SocialShareOption from "components/feed/feed-item/preview/SocialShareOption";

import ChqModalConfirmDelete from "components/common/ChqModalConfirmDelete";
import LightboxStoryQuickOptions from "components/common/gallery/LightboxStoryQuickOptions";
import { useProfile } from "app/context";
import useLatestMedia from "app/hooks/useLatestMedia";
import { deleteEntity, updateEntity } from "../mutations";
import EditApprovedMessageModal from "./EditApprovedMessageModal";
import { filtersToQuery, queryToOptions } from "../EmbedStoriesSlider";
import StoryFilterBar from "../StoryFilterBar";

const queryStringParsers = {
  id: "intList"
};

const getApprovedContentIds = queryString => {
  const { id } = queryString;

  return {
    approved_content_ids: id || null
  };
};

const updateShared = updateEntity("shared");
const deleteShared = deleteEntity("shared");

const SocialLink = ({ socialShare }) => {
  switch (socialShare.socialType) {
    case "facebook":
      return (
        <FacebookLink facebookId={socialShare.externalId}>
          <Trans>View on Facebook</Trans>
        </FacebookLink>
      );
    case "instagram":
      return (
        <InstagramLink link={socialShare.url}>
          <Trans>View on Instagram</Trans>
        </InstagramLink>
      );
    case "linkedin":
      return (
        <LinkedinLink linkedinId={socialShare.externalId}>
          <Trans>View on LinkedIn</Trans>
        </LinkedinLink>
      );
    case "tiktok":
      return (
        <TiktokLink link={socialShare.url}>
          <Trans>View on Tiktok</Trans>
        </TiktokLink>
      );
    default:
      break;
  }

  return <></>;
};

const ExternalSocialShareRow = ({ externalSocialShare, onDelete, onUpdateShared }) => {
  const { creator, createdAt, media: storyMedia, socialType, userActivityId } = externalSocialShare;

  const { approvedForSocial } = externalSocialShare;
  const story = approvedForSocial?.entity;

  const handleDelete = async () => {
    await makeDelete(`/stories/external_social_share/${externalSocialShare.id}`);
    onDelete(externalSocialShare);
  };

  const { profile } = useProfile();

  const {
    isFetching: mediaIsFetching,
    thirdPartyMedias,
    onThirdPartyMediasReload,
    onUpdateDraftVersion,
    lastMediaVersion
  } = useLatestMedia(story, profile, { landingPage: false });
  const isLastMediaVersionProcessing = lastMediaVersion?.status === "processing";

  if (!story) return <></>;

  return (
    <tr>
      <td>
        <div className="StoryTable-Details__Thumbnail-Container">
          <img className="StoryTable-Details__Image" src={storyMedia.thumbnail || storyMedia.url} alt={storyMedia.alt || externalSocialShare.message} />
          {storyMedia.mediaType === "video" && <Icon className="StoryTable-Details__PlayIcon" icon="play-no-border" />}
        </div>
      </td>
      <td className="StoryTable-Details">
        <p>{externalSocialShare.message}</p>
      </td>
      <td>
        <h4 className="User">
          <Trans>
            Shared by
            {" "}
            {creator.name}
          </Trans>
        </h4>
        <p><Time time={createdAt} format="%b %e %Y, %l:%M %p" keepYear /></p>
      </td>
      <td>
        <SocialChannel socialType={socialType} />
      </td>
      <td className="Actions-column">
        <LightboxStoryQuickOptions
          className="Admin-QuickOptions"
          isLastMediaVersionProcessing={isLastMediaVersionProcessing}
          story={story}
          thirdPartyMediaIsFetching={mediaIsFetching}
          thirdPartyMedias={thirdPartyMedias}
          onDelete={onDelete}
          onUpdate={onUpdateShared}
          onAnotherModalOpen={() => {}}
          onPauseVideo={() => {}}
          onThirdPartyMediasReload={onThirdPartyMediasReload}
          onUpdateDraftVersion={onUpdateDraftVersion}
        />
        <div className="admin-story-action-buttons Buttons">
          <ChqModalConfirmDelete
            onAccept={handleDelete}
            trigger={onTrigger => (
              <Button onClick={onTrigger} inverted><Icon icon="trash-a-outlined" /></Button>
            )}
          >
            <p><Trans>Are you sure you would like to delete this external social share?</Trans></p>
            <p><Trans>The activity feed is going to be deleted too</Trans></p>
          </ChqModalConfirmDelete>
        </div>
        <SocialLink socialShare={externalSocialShare} />
        {userActivityId && (
          <Link to={`/activity/${userActivityId}`}>
            <Trans>View on CultureHQ</Trans>
          </Link>
        )}
      </td>
    </tr>
  );
};

const SocialShareRow = ({ onDelete, socialShare, story, onUpdateShared }) => {
  const { creator, createdAt, socialType, userActivityId } = socialShare;
  const { media: storyMedia, question } = story;
  const { profile } = useProfile();

  const handleDelete = async () => {
    await makeDelete(`/stories/social_share/${socialShare.id}`);
    onDelete(socialShare);
  };

  const {
    isFetching: mediaIsFetching,
    thirdPartyMedias,
    onThirdPartyMediasReload,
    onUpdateDraftVersion,
    lastMediaVersion
  } = useLatestMedia(story, profile, { landingPage: false });
  const isLastMediaVersionProcessing = lastMediaVersion?.status === "processing";

  return (
    <tr>
      <td>
        <div className="StoryTable-Details__Thumbnail-Container">
          <img className="StoryTable-Details__Image" src={storyMedia.thumbnail || storyMedia.url} alt={storyMedia.alt || question.question} />
          {storyMedia.mediaType === "video" && <Icon className="StoryTable-Details__PlayIcon" icon="play-no-border" />}
          {isMultiImageStory(story) && <Icon className="StoryTable-Details__ImagesIcon" icon="images" />}
        </div>
      </td>
      <td className="StoryTable-Details">
        <p>{socialShare.message}</p>
      </td>
      <td>
        <h4 className="User">
          <Trans>Shared by</Trans>
          {" "}
          {creator.name}
        </h4>
        <p><Time time={createdAt} format="%b %e %Y, %l:%M %p" keepYear /></p>
      </td>
      <td>
        <SocialChannel socialType={socialType} />
      </td>
      <td className="Actions-column">
        <LightboxStoryQuickOptions
          className="Admin-QuickOptions"
          isLastMediaVersionProcessing={isLastMediaVersionProcessing}
          story={story}
          thirdPartyMediaIsFetching={mediaIsFetching}
          thirdPartyMedias={thirdPartyMedias}
          onDelete={onDelete}
          onUpdate={onUpdateShared}
          onAnotherModalOpen={() => {}}
          onPauseVideo={() => {}}
          onThirdPartyMediasReload={onThirdPartyMediasReload}
          onUpdateDraftVersion={onUpdateDraftVersion}
        />
        <div className="admin-story-action-buttons Buttons">
          <ChqModalConfirmDelete
            onAccept={handleDelete}
            trigger={onTrigger => (
              <Button onClick={onTrigger} inverted><Icon icon="trash-a-outlined" /></Button>
            )}
          >
            <p><Trans>Are you sure you would like to delete this social share?</Trans></p>
            <p><Trans>The activity feed is going to be deleted too</Trans></p>
          </ChqModalConfirmDelete>
        </div>
        <SocialLink socialShare={socialShare} />
        {userActivityId && (
          <Link to={`/activity/${userActivityId}`}>
            <Trans>View on CultureHQ</Trans>
          </Link>
        )}
      </td>
    </tr>
  );
};

const ApprovedForSocialMessageStatus = ({
  activityId, creator, scheduledAt, shared, socialShareType
}) => {
  if (scheduledAt) {
    if (shared && socialShareType === "personal") {
      if (activityId) {
        return (
          <>
            <h4 className="User">
              <Trans>Already shared by</Trans>
              {" "}
              {creator.name}.
            </h4>
            <p>
              <Trans>Scheduled at</Trans>
              {" "}
              <Time time={scheduledAt} format="%b %e %Y, %l:%M %p" />
            </p>
          </>
        );
      }

      return (
        <span style={{ color: "#c14c4c" }}>
          <h4 className="User">
            <Trans>{creator.name} it is time to share.</Trans>
          </h4>
          <p>
            <Trans>Scheduled at</Trans>
            {" "}
            <Time time={scheduledAt} format="%b %e %Y, %l:%M %p" />
          </p>
        </span>
      );
    }

    return (
      <>
        <h4 className="User">
          <Trans>Scheduled by</Trans>
          {" "}
          {creator.name}
        </h4>
        <p>
          <Time time={scheduledAt} format="%b %e %Y, %l:%M %p" />
        </p>
      </>
    );
  }

  return (
    <h4 className="User">
      <Trans>Waiting to be shared</Trans>
    </h4>
  );
};

const ApprovedContentRow = ({ onDelete, onUpdateShared, sharedContent, story }) => {
  const { profile } = useProfile();
  const { media: storyMedia, question } = story;
  const {
    isFetching: mediaIsFetching,
    thirdPartyMedias,
    onThirdPartyMediasReload,
    onUpdateDraftVersion,
    lastMediaVersion
  } = useLatestMedia(story, profile, { landingPage: false });
  const isLastMediaVersionProcessing = lastMediaVersion?.status === "processing";
  const {
    activityId,
    approvedForSocial,
    message,
    socialType,
    userActivityId,
    scheduledAt,
    shared,
    socialShareType,
    creator
  } = sharedContent;

  const onUpdateApprovedForSocialMessage = approvedForSocialMessage => {
    onUpdateShared(approvedForSocialMessage);
  };

  const handleDelete = async () => {
    await makeDelete(`/stories/shared/${sharedContent.id}`);
    onDelete(sharedContent);
  };

  if (scheduledAt && shared && socialShareType === "personal" && (socialType === "linkedin" || socialType === "tiktok")) {
    return <></>;
  }

  if (scheduledAt && shared && socialShareType === "business") {
    return <></>;
  }

  return (
    <tr>
      <td>
        <div className="StoryTable-Details__Thumbnail-Container">
          <img className="StoryTable-Details__Image" src={storyMedia.thumbnail || storyMedia.url} alt={storyMedia.alt || question.question} />
          {storyMedia.mediaType === "video" && <Icon className="StoryTable-Details__PlayIcon" icon="play-no-border" />}
          {isMultiImageStory(story) && <Icon className="StoryTable-Details__ImagesIcon" icon="images" />}
        </div>
      </td>
      <td className="StoryTable-Details">
        <div className="StoryTable-Details__Header">
          <h4 className="StoryTable-Details__Title">{story.question.question}</h4>
          {(story.question.status === "hidden" || story.question.parentStoryQuestion?.status === "hidden") && (
            <Tag className="ChildPrompts__Tag chq-tag-bk"><Trans>Hidden</Trans></Tag>
          )}
        </div>
        <p>{message}</p>
      </td>
      <td>
        <ApprovedForSocialMessageStatus
          activityId={activityId}
          scheduledAt={scheduledAt}
          shared={shared}
          socialShareType={socialShareType}
          creator={creator}
        />
      </td>
      <td>
        <SocialChannel socialType={socialType} />
      </td>
      <td className="Actions-column">
        <LightboxStoryQuickOptions
          className="Admin-QuickOptions"
          isLastMediaVersionProcessing={isLastMediaVersionProcessing}
          story={story}
          thirdPartyMediaIsFetching={mediaIsFetching}
          thirdPartyMedias={thirdPartyMedias}
          onDelete={onDelete}
          onUpdate={onUpdateShared}
          onAnotherModalOpen={() => {}}
          onPauseVideo={() => {}}
          onThirdPartyMediasReload={onThirdPartyMediasReload}
          onUpdateDraftVersion={onUpdateDraftVersion}
        />
        <div className="admin-story-action-buttons Buttons">
          {socialShareType === "personal" && scheduledAt && shared && !activityId && (
            <SocialShareOption
              approvedForSocial={approvedForSocial}
              approvedMessage={sharedContent}
              story={story}
            >
              <Button inverted><Icon icon="share-outlined" /></Button>
            </SocialShareOption>
          )}
          <ChqModalConfirmDelete
            onAccept={handleDelete}
            trigger={onTrigger => (
              <Button onClick={onTrigger} inverted><Icon icon="trash-a-outlined" /></Button>
            )}
          >
            <p>
              <Trans>
                Are you sure you would like to delete
                this approved content to be shared
              </Trans>
            </p>
            <p><Trans>The activity feed is going to be deleted too</Trans></p>
          </ChqModalConfirmDelete>
          <EditApprovedMessageModal
            approvedContentMessage={sharedContent}
            onUpdateApprovedForSocialMessage={onUpdateApprovedForSocialMessage}
            story={story}
          >
            {onTrigger => (
              <Button onClick={onTrigger} inverted><Icon icon="edit-outlined" /></Button>
            )}
          </EditApprovedMessageModal>
        </div>
        {userActivityId && (
          <Link to={`/activity/${userActivityId}`}>
            <Trans>View on CultureHQ</Trans>
          </Link>
        )}
        {activityId && (
          <Link to={`/activity/${activityId}`}>
            <Trans>View on CultureHQ</Trans>
          </Link>
        )}
      </td>
    </tr>
  );
};

const InternalShare = ({ onDelete, onUpdateShared, sharedContent }) => {
  const {
    approvedForSocial: { entity: story }, socialShares
  } = sharedContent;

  if (socialShares?.length > 0) {
    return (
      <>
        <ApprovedContentRow
          onDelete={onDelete}
          onUpdateShared={onUpdateShared}
          sharedContent={sharedContent}
          story={story}
        />
        {socialShares.map(socialShare => (
          <SocialShareRow
            key={`social-share-${socialShare.id}`}
            onDelete={onDelete}
            onUpdateShared={onUpdateShared}
            socialShare={socialShare}
            story={story}
          />
        ))}
      </>
    );
  }

  return (
    <ApprovedContentRow
      onDelete={onDelete}
      onUpdateShared={onUpdateShared}
      sharedContent={sharedContent}
      story={story}
    />
  );
};

const PublishedContent = ({ onDelete, onUpdateShared, sharedContent }) => {
  if (sharedContent.approvedForSocial) {
    return (
      <InternalShare
        key={`internal-${sharedContent.id}`}
        sharedContent={sharedContent}
        onDelete={onDelete}
        onUpdateShared={onUpdateShared}
      />
    );
  }

  return (
    <ExternalSocialShareRow
      key={`external_share-${sharedContent.id}`}
      externalSocialShare={sharedContent}
      onDelete={onDelete}
      onUpdateShared={onUpdateShared}
    />
  );
};

const StoriesPublishedTable = ({ match, queryString }) => {
  const [page, onPageClick] = usePagination();
  const [selectedFilters, setSelectedFilters] = useState([]);
  const {
    fetching,
    json: { shared = [], pagination = {} },
    onUpdate: onSharedUpdate
  } = useFetch(
    () => {
      const query = filtersToQuery(selectedFilters);
      const params = {
        ...getApprovedContentIds(queryString),
        ...queryToOptions(query),
        page: page || 1
      };
      const [bodySearch] = selectedFilters;
      if (typeof bodySearch === "string") params.bodySearch = bodySearch;
      return makeGet("/stories/shared", params);
    },
    [page, selectedFilters]
  );

  const onUpdateShared = content => onSharedUpdate(updateShared(content));
  const onDeleteShared = content => onSharedUpdate(deleteShared(content));

  const handleSelectedTags = (updatedOptions, _) => {
    onPageClick(1);
    setSelectedFilters(updatedOptions);
  };

  return (
    <div className="padding-left-15px padding-right-15px StoryApprovals">
      <PageHeader title={t("Stories for Social")}>
        <Trans>
          Below is the list of stories that have been shared in social or approved for
          others to share on social. You have a history of the shared stories and you are
          able to change the default message for stories that the users are able to share.
        </Trans>
        <Link className="Link-Social__Media" to="/profile/settings/social">
          <Icon icon="plus" />
          <Trans>Link social accounts</Trans>
        </Link>
        <StoryFilterBar
          creatable
          selectedOptions={selectedFilters}
          handleSelectedTags={handleSelectedTags}
          icon="ios-search-strong"
        />
      </PageHeader>
      <Loader loading={fetching}>
        <Table className="StoryTable StoryPublishedTable">
          <thead>
            <tr>
              <th className="thumb">
                <Trans>Post</Trans>
              </th>
              <th />
              <th className="users">
                <Trans>Status</Trans>
              </th>
              <th className="users">
                <Trans>Social Channel</Trans>
              </th>
              <th className="actions">
                <Trans>Actions</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {shared.map((content, index) => (
              <PublishedContent
                key={index}
                sharedContent={content}
                onDelete={onDeleteShared}
                onUpdateShared={onUpdateShared}
              />
            ))}
          </tbody>
        </Table>
        {shared.length < 1 && (
          <Warning>
            <Trans>
              No stories have been shared yet.
            </Trans>
          </Warning>
        )}
        <Pagination {...pagination} onClick={onPageClick} />
      </Loader>
    </div>
  );
};

const StoriesPublished = props => (
  <QueryStringProvider parsers={queryStringParsers}>
    <QueryStringConsumer>
      {queryStringContext => (
        <StoriesPublishedTable
          {...props}
          {...queryStringContext}
        />
      )}
    </QueryStringConsumer>
  </QueryStringProvider>
);

export default StoriesPublished;
